import { FC, ReactNode } from "react";
import styled from "styled-components";
import { WhiteIcon, IconName } from "../Icon";
import { BaseComponentProps } from "../types";
import { ButtonVariant } from "./types";
import { primaryButtonActive, primaryButtonHover } from "./commonStyles";
import { whiteActive } from "../../typography/alpha";
import { Spinner } from "../Loader";

export type ButtonProps = BaseComponentProps<
  "button",
  {
    /** Content rendered within the button. Likely just text. */
    children: ReactNode;

    /** Icon to render on the left side. */
    iconName?: IconName;

    /** Icon to render on the right side. */
    secondaryIconName?: IconName;

    variant?: ButtonVariant;

    loading?: boolean;
  }
>;

export const PrimaryButton: FC<ButtonProps> = ({
  children,
  iconName,
  secondaryIconName,
  variant = ButtonVariant.DEFAULT,
  disabled = false,
  loading = false,
  ...buttonProps
}) => (
  <StyledButton
    {...buttonProps}
    variant={variant}
    disabled={variant === ButtonVariant.DISABLED || disabled || loading}
    className={loading ? "loading" : ""}
  >
    {loading && <ButtonSpinner />}
    {iconName && <WhiteIcon iconName={iconName} iconSize="small" plain />}
    <span>{children}</span>
    {secondaryIconName && (
      <WhiteIcon iconName={secondaryIconName} iconSize="small" plain />
    )}
  </StyledButton>
);

export const StyledButton = styled.button<ButtonProps>`
  position: relative;
  background: var(--color-primary-800);
  border: 1px solid var(--color-primary-800);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  margin: 0;
  height: 32px;
  padding: 0px 16px;
  border-radius: var(--size-border-radius-small);

  ${whiteActive.body3};

  > *:last-child {
    line-height: 16px;
  }

  > *:not(:last-child) {
    margin-right: 4px;
  }

  :focus {
    outline: none;
  }

  :disabled {
    cursor: not-allowed;
    background: var(--color-primary-200);
    border: 1px solid var(--color-primary-200);

    &.loading {
      background-color: var(--color-primary-400);
      border: 1px solid var(--color-primary-400);
    }
  }

  :not(:disabled) {
    &:hover {
      ${primaryButtonHover}
    }
    &:active {
      ${primaryButtonActive}
    }
  }
`;

export const ButtonSpinner = styled(Spinner)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  && ~ * {
    visibility: hidden;
  }
`;
